import { Box, Flex, Table, Text } from "@chakra-ui/react"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import UserAvatarStack from "@pathwright/ux/src/components/avatar/UserAvatarStack"
import { AvatarUser } from "@pathwright/ux/src/components/avatar/types"
import Link, { LinkProps } from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { motion } from "framer-motion"
import { ReactNode } from "react"
import { usePathwrightIntercomGuidanceTeamMembersQuery } from "@pathwright/graphql-client/src/generated"
import { useIntercomContext } from "../../intercom/IntercomProvider"
import { useGetNewResource } from "../../path/create/CreateOrSelectResource"
import { usePathCreatorContext } from "../../path/create/PathCreatorContext"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import Tip, { TipProps } from "../../tips/Tip"

function usePathwrightIntercomTeamMemberAvatarUsers() {
  const { usingPathwrightIntercom } = useIntercomContext()
  const pathwrightIntercomGuidanceTeamMembersQuery =
    usePathwrightIntercomGuidanceTeamMembersQuery({
      skip: !usingPathwrightIntercom
    })
  const pwIntercomGuidanceTeamMembers =
    pathwrightIntercomGuidanceTeamMembersQuery.data?.integrations?.intercom
      ?.team || []

  // Map to AvatarUser[] to be used in UserAvatarStack.
  const avatarUsers: AvatarUser[] = pwIntercomGuidanceTeamMembers.map(
    (member) => ({
      id: member.id,
      first_name: member.name.split(/ +/)[0],
      last_name: member.name.split(/ +/).slice(1).join(" "),
      full_name: member.name,
      profile: {
        image: member.avatar
      }
    })
  )

  return avatarUsers
}

// For formatting each row in the tip table, each row having 3 columns
// and behaving like a link/button.
function TipRow({
  tabelCellColumns,
  ...linkProps
}: {
  tabelCellColumns: [ReactNode, ReactNode, ReactNode]
} & LinkProps) {
  return (
    <Table.Row
      css={{
        "&:last-of-type td": {
          // No border for tds in last row.
          borderBottomWidth: "0px"
        }
      }}
      p={0}
      // NOTE: border-radius doesn't apply to table rows.
      borderRadius="md"
      bg="transparent"
      _hover={{ bg: "whiteAlpha.100" }}
    >
      <Table.Cell
        pos="relative"
        py={0}
        px={2}
        borderColor="whiteAlpha.400"
        _groupHover={{ borderColor: "transparent" }}
      >
        <Flex justifyContent="center">{tabelCellColumns[0]}</Flex>
        <Link as="a" pos="absolute" inset={0} p={0} {...linkProps} />
      </Table.Cell>
      {/* Allowing middle text column to be responsible for padding the row top/bottom,
      making space for the demo avatar to grow without increasing the row height (within margin of error). */}
      <Table.Cell
        pos="relative"
        py={4}
        px={2}
        borderColor="whiteAlpha.400"
        _groupHover={{ borderColor: "transparent" }}
      >
        <Text color="inherit" m={0}>
          {tabelCellColumns[1]}
        </Text>
        <Link as="a" pos="absolute" inset={0} p={0} {...linkProps} />
      </Table.Cell>
      <Table.Cell
        pos="relative"
        py={0}
        px={2}
        borderColor="whiteAlpha.400"
        _groupHover={{ borderColor: "transparent" }}
      >
        {tabelCellColumns[2]}
        <Link as="a" pos="absolute" inset={0} p={0} {...linkProps} />
      </Table.Cell>
    </Table.Row>
  )
}

// Only intended for Pathwright Intercom.
function PathwrightIntercomTeamMembers() {
  const avatarUsers = usePathwrightIntercomTeamMemberAvatarUsers()

  return avatarUsers.length ? (
    <UserAvatarStack users={avatarUsers} max={avatarUsers.length} size="2xs" />
  ) : null
}

// For activating the "Add Path" flow.
function useAddPath() {
  const { setInitialValue } = usePathCreatorContext()
  const { getNewResource } = useGetNewResource()
  async function addPath() {
    setInitialValue(await getNewResource())
  }

  return addPath
}

function HomeWelcomeDesignerBlankSlateTip(props: Partial<TipProps>) {
  const { me, school } = usePathwrightContext()
  const avatarUsers = usePathwrightIntercomTeamMemberAvatarUsers()
  // Grab Michelle's avatar.
  const michelleAvatarUser = avatarUsers.find(
    (user) => user.first_name === "Michelle"
  )
  const addPath = useAddPath()

  return (
    <Tip
      tip={{
        id: "home-welcome-designer-blank-slate",
        title: `Welcome, ${me.first_name}!`,
        content: (
          <Box>
            <Text color="inherit" mt={0}>
              This Home screen helps you keep track of all your learning Paths.
              {!!avatarUsers.length && " Here are three ways to get started:"}
            </Text>
            <Box mt={4}>
              {avatarUsers.length ? (
                <motion.div
                  key="content"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <Table.Root className="group">
                    <Table.Body>
                      <TipRow
                        tabelCellColumns={[
                          <Pathicon icon="plus-circle-fill" size="1.6em" />,
                          "Get started right away by adding a Path.",
                          <Pathicon icon="chevron-right" size="1.2em" />
                        ]}
                        onClick={addPath}
                      />
                      <TipRow
                        tabelCellColumns={[
                          <PathwrightIntercomTeamMembers />,
                          "Ask any question and get an answer from an expert Path Guide or our Help Center.",
                          <Pathicon icon="chevron-right" size="1.2em" />
                        ]}
                        onClick={() => {
                          window.Intercom?.("showNewMessage")
                        }}
                      />
                      {!!michelleAvatarUser && (
                        <TipRow
                          tabelCellColumns={[
                            <UserAvatar user={michelleAvatarUser} size="sm" />,
                            "Schedule a free, personal demo and Q&A time with Michelle.",
                            <Pathicon icon="chevron-right" size="1.2em" />
                          ]}
                          href={`https://calendly.com/michellejjohnson/one-on-one?utm_source=platform&utm_medium=website&utm_campaign=home_welcome_tip&utm_content=school-${school.subdomain}-${school.id}`}
                          target="_blank"
                          rel="noopener"
                        />
                      )}
                    </Table.Body>
                  </Table.Root>
                </motion.div>
              ) : (
                <Text color="inherit">
                  To get started,{" "}
                  <Link variant="underline" onClick={addPath}>
                    add a Path
                    {/* <Button variant="plain" onClick={addPath} p={0}>
                    </Button> */}
                  </Link>
                  {
                    " — you can create a new one or add an existing Path from your Library."
                  }
                </Text>
              )}
            </Box>
          </Box>
        )
      }}
      icon="lightbulb-on"
      layout="dismissable"
      colorPalette="dark"
      {...props}
    />
  )
}

export default HomeWelcomeDesignerBlankSlateTip
